/**
 * When the money actually lands, and therefore when a premium may be drafted.
 *
 * WHY THIS FILE EXISTS
 * A final expense book is retirees on a fixed income. The premium is drafted on
 * a day the agent picked at a kitchen table — very often the 1st or the 5th,
 * because those sound tidy. The Social Security Administration pays on a rule
 * that has nothing to do with tidy: the day of the month you were BORN.
 *
 * For somebody born on the 5th the money does not arrive until the second
 * Wednesday — the 8th at the earliest, the 14th at the latest. A draft set for
 * the 1st or the 5th is therefore taken before they have been paid, in January
 * and in February and every month after that, until the policy lapses and the
 * agent is charged back for business they wrote correctly, priced correctly and
 * sold honestly.
 *
 * None of that is bad luck. It is arithmetic on a date of birth that is already
 * on the application, and it is done by nobody — not the carriers, whose draft
 * setup has no birth-date logic in it, and not any agency software found in a
 * survey of twenty-five vendors.
 *
 * AND THE HALF THAT IS COUNTER-INTUITIVE, which is why a rule of thumb does not
 * work here. Somebody born on the 25th is paid on the fourth Wednesday, between
 * the 22nd and the 28th. Their risky days are not the 1st and the 5th — by then
 * last month's deposit has been sitting there for a week. Their risky days are
 * the 15th to the 21st, the longest this household ever goes without being paid.
 * So "avoid early in the month" is exactly wrong for a third of clients, and an
 * agent applying one heuristic to everybody will put some drafts on the single
 * worst day available to that person.
 *
 * WHY IT MATTERS MORE THAN IT LOOKS
 * Roughly seven in ten life policy lapses are non-payment rather than a
 * decision to cancel, and the peer-reviewed work on why policies die puts
 * 37.8% down to forgetfulness against 15.4% from an income shock (Gottlieb &
 * Smetters, American Economic Review 111(8), 2021). The largest single cause of
 * policy death is an operational failure — which is the most fixable kind there
 * is, and this is the most operational fix available.
 *
 * WHAT THIS MODULE WILL NOT DO
 * It will not guess. If nobody has asked the client how they are paid, it says
 * so and offers nothing, exactly as the quoter refuses to invent a premium.
 * A confident wrong draft date is worse than no opinion, because the agent
 * would have asked otherwise.
 *
 * THE RULES, from the SSA's published payment schedule:
 *   born 1st–10th   → second Wednesday
 *   born 11th–20th  → third Wednesday
 *   born 21st–31st  → fourth Wednesday
 *   drawing benefits since before May 1997, or drawing both SS and SSI
 *                   → the 3rd of the month
 *   SSI only        → the 1st of the month
 *
 * When a fixed date (the 1st or the 3rd) falls on a weekend or a federal
 * holiday the payment moves EARLIER, to the preceding business day. Earlier is
 * always safe for our purpose, so the windows below hold regardless.
 */

/**
 * How this person's money arrives, as an agent would establish at the table.
 *
 * WHY THIS IS NOT JUST SOCIAL SECURITY
 * The first version of this file only knew the SSA schedule, which covers a
 * final expense book and roughly nobody else. A real agency writes term and
 * indexed universal life to people of every age, and a wage earner's draft can
 * bounce for exactly the same reason a retiree's can. So the question is not
 * "are they on Social Security" — it is "when does their money arrive, and how
 * often". Social Security is only the case where the answer is already on the
 * application rather than something you have to ask for.
 */
export type BenefitSchedule =
  /** Social Security on the modern rule — the Wednesday set by their birthday. */
  | 'ss_birthday'
  /** On benefits since before May 1997, or drawing both SS and SSI: the 3rd. */
  | 'ss_third'
  /** SSI only: the 1st. */
  | 'ssi_first'
  /** Salaried, paid twice a month — the 1st and the 15th. */
  | 'semi_monthly'
  /** Paid every other Friday. Twenty-six deposits a year, drifting. */
  | 'biweekly'
  /** Paid every Friday. */
  | 'weekly'
  /** Commission, self-employed, irregular — asked, and there is no cadence. */
  | 'other';

export const BENEFIT_SCHEDULES: readonly BenefitSchedule[] = [
  'ss_birthday', 'ss_third', 'ssi_first', 'semi_monthly', 'biweekly', 'weekly', 'other',
];

export const BENEFIT_SCHEDULE_LABEL: Record<BenefitSchedule, string> = {
  ss_birthday: 'Social Security, paid on the Wednesday set by their birthday',
  ss_third: 'Social Security since before May 1997, or Social Security and SSI',
  ssi_first: 'SSI only — paid on the 1st',
  semi_monthly: 'Salaried, paid on the 1st and the 15th',
  biweekly: 'Paid every other Friday',
  weekly: 'Paid every Friday',
  other: 'Commission, self-employed or irregular',
};

/**
 * What an agent actually asks. The labels above are precise; these are what the
 * question sounds like out loud, because "are you a pre-May-1997 beneficiary"
 * is not a thing anybody says at a kitchen table.
 */
export const BENEFIT_SCHEDULE_PROMPT: Record<BenefitSchedule, string> = {
  ss_birthday: 'Social Security — it comes on a Wednesday',
  ss_third: 'Social Security — it comes on the 3rd',
  ssi_first: 'SSI — it comes on the 1st',
  semi_monthly: 'They get paid on the 1st and the 15th',
  biweekly: 'They get paid every other Friday',
  weekly: 'They get paid every Friday',
  other: 'Commission or irregular — it varies',
};

/**
 * How often the money lands, which decides what kind of advice is possible.
 *
 *   'monthly'  one deposit a month on a knowable day. A draft set before it
 *              fails EVERY month — the strongest thing this module can say.
 *   'twice'    two deposits a month on fixed dates. Nothing outright fails,
 *              because the money is never more than about a fortnight old;
 *              some days are still better than others.
 *   'drifting' paid every week or fortnight, so payday moves through the
 *              calendar. No fixed draft day can be aligned to it — and none
 *              needs to be, because they are never long without money.
 *   'none'     no cadence to reason about.
 */
type Cadence = 'monthly' | 'twice' | 'drifting' | 'none';

const CADENCE: Record<BenefitSchedule, Cadence> = {
  ss_birthday: 'monthly',
  ss_third: 'monthly',
  ssi_first: 'monthly',
  semi_monthly: 'twice',
  biweekly: 'drifting',
  weekly: 'drifting',
  other: 'none',
};

/* --------------------------------------------- reading a day off a birthday -- */

/**
 * The day of the month somebody was born, from their date of birth.
 *
 * This looks too small to be a function and it is not, because the obvious
 * spelling of it is wrong. A `DATE` column carries no time and no zone, and it
 * arrives here as a Date pinned to UTC midnight — `1955-03-05T00:00:00Z`.
 * Reading that with `getDate()` asks "what day was it HERE at that instant",
 * and in every timezone west of Greenwich the answer is the 4th.
 *
 * So a client born on the 11th would be read as the 10th, which moves them out
 * of the third-Wednesday bucket and into the second, and the software would
 * confidently recommend a draft date a full week before their money lands. The
 * failure would only show for clients born on the 1st, 11th and 21st, only for
 * agencies west of UTC, and only as lapses months later — which is to say it
 * would never be found.
 *
 * One function, used by the server and the browser both, so the two cannot
 * disagree.
 */
export function dobDayOfMonth(dob: Date | null | undefined): number | null {
  if (!dob || Number.isNaN(dob.getTime())) return null;
  return dob.getUTCDate();
}

/* ------------------------------------------------------- when it arrives -- */

/** The earliest and latest day of the month a deposit can land. */
export interface ArrivalWindow {
  earliest: number;
  latest: number;
  /** How to describe it to a person, without jargon. */
  label: string;
}

/**
 * The nth Wednesday of a month can only fall in a seven-day span, and that span
 * is what matters — not the date in any one month.
 *
 * This is the part everyone gets wrong when they do it by hand. An agent who
 * checks one month's calendar and sets the draft two days after that month's
 * Wednesday has built something that works in January and fails in March,
 * because the Wednesday moved. The failure looks random. It is not.
 */
function nthWednesdayWindow(n: 2 | 3 | 4): { earliest: number; latest: number } {
  // The 1st of a month is some weekday; the first Wednesday therefore falls
  // between the 1st and the 7th, and the nth is exactly 7(n-1) days later.
  return { earliest: 1 + 7 * (n - 1), latest: 7 + 7 * (n - 1) };
}

/** Every day-range in a month on which this person's money can land. */
export function arrivalWindows(
  schedule: BenefitSchedule,
  dobDayOfMonth: number | null,
): ArrivalWindow[] {
  switch (schedule) {
    case 'ssi_first':
      // Moves EARLIER onto the preceding business day when the 1st is a weekend
      // or holiday, so the last working day of the previous month is possible.
      // Treated as "the 1st" because earlier is never the problem.
      return [{ earliest: 1, latest: 1, label: 'the 1st of the month' }];
    case 'ss_third':
      return [{ earliest: 1, latest: 3, label: 'the 3rd of the month' }];
    case 'semi_monthly':
      return [
        { earliest: 1, latest: 1, label: 'the 1st' },
        { earliest: 15, latest: 15, label: 'the 15th' },
      ];
    case 'biweekly':
    case 'weekly':
    case 'other':
      // No fixed calendar anchor. Cadence decides the advice, not a window.
      return [];
    case 'ss_birthday': {
      if (dobDayOfMonth === null) return [];
      if (!Number.isInteger(dobDayOfMonth) || dobDayOfMonth < 1 || dobDayOfMonth > 31) return [];
      const n = dobDayOfMonth <= 10 ? 2 : dobDayOfMonth <= 20 ? 3 : 4;
      const w = nthWednesdayWindow(n);
      const ord = n === 2 ? 'second' : n === 3 ? 'third' : 'fourth';
      return [{ ...w, label: `the ${ord} Wednesday — the ${w.earliest}th to the ${w.latest}th` }];
    }
    default:
      return [];
  }
}

/**
 * The single arrival window, for the monthly schedules that have exactly one.
 * Kept separate because a screen showing "when their money arrives" wants one
 * phrase, and because it is what every existing caller already asks for.
 */
export function arrivalWindow(
  schedule: BenefitSchedule,
  dobDayOfMonth: number | null,
): ArrivalWindow | null {
  const all = arrivalWindows(schedule, dobDayOfMonth);
  return all.length === 1 ? all[0]! : null;
}

/* ---------------------------------------------------------- the verdict -- */

export type DraftVerdict = 'safe' | 'tight' | 'fails' | 'unknown';

export interface DraftAdvice {
  verdict: DraftVerdict;
  /** Days of the month that are safe every month, in order of preference. */
  safeDays: number[];
  /** When their money arrives, for showing beside the advice. */
  arrival: ArrivalWindow | null;
  /** One sentence a person can read out to a client. */
  message: string;
}

/**
 * The safe window is the seven days following the LATEST the money can arrive.
 *
 * Latest, not earliest, and that is the whole point. Sizing the window off the
 * earliest arrival produces a draft date that clears in some months and bounces
 * in others, which is worse than one that never clears — nobody diagnoses an
 * intermittent failure, they just watch the policy die.
 *
 * The window wraps past the end of the month, because for anyone born on or
 * after the 21st there is barely any room left in their own month. Their
 * correct draft date is early in the FOLLOWING month, which is still after
 * their deposit and is the answer almost nobody reaches on their own.
 */
function safeWindow(arrival: ArrivalWindow): number[] {
  const out: number[] = [];
  for (let i = 1; i <= 7; i += 1) {
    const day = arrival.latest + i;
    // Days 29–31 do not exist every month, so a draft set there silently
    // moves or fails depending on the carrier. Wrap instead.
    out.push(day > 28 ? day - 28 : day);
  }
  return out;
}

/**
 * Should the premium be drafted on this day of the month?
 *
 * `draftDay` may be null, which is the state before anybody has chosen — the
 * advice is then purely a recommendation rather than a warning about a decision
 * already made.
 */
export function adviseDraftDay(
  schedule: BenefitSchedule | null,
  dobDayOfMonth: number | null,
  draftDay: number | null,
): DraftAdvice {
  if (schedule === null) {
    return {
      verdict: 'unknown',
      safeDays: [],
      arrival: null,
      message: 'Nobody has asked how this person is paid, so there is no safe draft date to offer. '
        + 'Ask when their money arrives each month.',
    };
  }

  const cadence = CADENCE[schedule];

  /*
   * PAID WEEKLY OR FORTNIGHTLY — say the useful thing, which is "stop worrying".
   *
   * Their payday moves through the calendar, so no fixed draft day can be
   * aligned to it and none needs to be: they are never more than a fortnight
   * from money. Inventing a recommendation here would be worse than useless —
   * it would spend the agent's attention on the clients who do not need it, and
   * teach them to ignore the warnings on the clients who do.
   */
  if (cadence === 'drifting') {
    const every = schedule === 'weekly' ? 'every week' : 'every other week';
    return {
      verdict: 'safe',
      safeDays: [],
      arrival: null,
      message: `They are paid ${every}, so their payday moves through the month and no draft date `
        + 'can be lined up with it. It does not need to be — they are never long without money. '
        + 'Any day works.',
    };
  }

  const windows = arrivalWindows(schedule, dobDayOfMonth);
  if (windows.length === 0) {
    const why = schedule === 'other'
      ? 'Their income is irregular, so there is no pay date to work from. Ask them which days are safe.'
      : 'Their date of birth is not on the record, and the payment date is set by it.';
    return { verdict: 'unknown', safeDays: [], arrival: null, message: why };
  }

  // Every day within a week after any deposit, in order of preference.
  const safeDays = [...new Set(windows.flatMap(safeWindow))];
  const arrival = windows.length === 1 ? windows[0]! : null;
  const arriveText = windows.map((w) => w.label).join(' and ');
  const list = safeDays.length > 4
    ? `${safeDays.slice(0, 4).join(', ')} or ${safeDays[4]}`
    : safeDays.join(' or ');

  if (draftDay === null) {
    return {
      verdict: 'unknown',
      safeDays,
      arrival,
      message: `Their money arrives ${arriveText}. Set the draft for the ${list} — after it lands.`,
    };
  }

  const inAWindow = windows.some((w) => draftDay >= w.earliest && draftDay <= w.latest);

  if (safeDays.includes(draftDay)) {
    return {
      verdict: 'safe',
      safeDays,
      arrival,
      message: `The ${ordinal(draftDay)} is just after their money arrives ${arriveText}.`,
    };
  }

  // On a payday itself: a race between the deposit and the draft. Clears some
  // months, bounces others — the worst kind, because nobody diagnoses it.
  if (inAWindow) {
    return {
      verdict: 'tight',
      safeDays,
      arrival,
      message: `The ${ordinal(draftDay)} lands on the same day their money arrives (${arriveText}). `
        + `Some months it will clear and some months it will not. Move it to the ${list}.`,
    };
  }

  /*
   * PAID TWICE A MONTH — nothing outright fails, and pretending otherwise
   * would be dishonest. With deposits on the 1st and the 15th the money is
   * never more than a fortnight old, so a badly chosen day is merely not ideal.
   * A "fails" verdict here would cry wolf and devalue the real ones.
   */
  if (cadence === 'twice') {
    return {
      verdict: 'safe',
      safeDays,
      arrival,
      message: `The ${ordinal(draftDay)} is not the strongest day, but they are paid twice a month `
        + `(${arriveText}) so it is never long since they were paid. The best days are the ${list}.`,
    };
  }

  // One deposit a month, and the draft comes before it. This is the case the
  // whole feature exists for: it fails every month, not occasionally.
  return {
    verdict: 'fails',
    safeDays,
    arrival,
    message: `Their money does not arrive until ${arriveText}, so a draft on the ${ordinal(draftDay)} `
      + `is taken before they have been paid — every month, not occasionally. Move it to the ${list}.`,
  };
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/* ------------------------------------------------- measuring the outcome -- */

/**
 * Whether moving a draft from one day to another was an improvement.
 *
 * Ranked, so "fails → tight" counts as better and "safe → tight" counts as
 * worse. `unknown` sits outside the ranking entirely: it means nobody has
 * recorded how the client is paid, so a move into or out of it is not a
 * judgement about the draft date at all and must not be counted as either.
 * Reporting an unknown as an improvement is how a number stops being true.
 */
const VERDICT_RANK: Record<DraftVerdict, number> = { fails: 0, tight: 1, safe: 2, unknown: -1 };

export type DraftMove = 'improved' | 'worsened' | 'no_change' | 'not_comparable';

export function classifyDraftMove(before: DraftVerdict, after: DraftVerdict): DraftMove {
  if (before === 'unknown' || after === 'unknown') return 'not_comparable';
  const a = VERDICT_RANK[before];
  const b = VERDICT_RANK[after];
  if (b > a) return 'improved';
  if (b < a) return 'worsened';
  return 'no_change';
}

export interface DraftExposure {
  /** Policies whose draft day is checkable at all — a schedule is recorded. */
  checked: number;
  fails: number;
  tight: number;
  safe: number;
  /** No pay schedule recorded, so nothing can be said. Not a clean bill. */
  unknown: number;
}

export const EMPTY_DRAFT_EXPOSURE: DraftExposure = {
  checked: 0, fails: 0, tight: 0, safe: 0, unknown: 0,
};

/**
 * How much of a book is drafting on a day the money is not there.
 *
 * This is the standing number, and it is the one that opens a conversation
 * with an agency that has never heard of Cellvora: *your book has forty-three
 * policies drafting on a day their client is not paid.* It needs no history
 * and no pilot — it is computable from a date of birth and a draft day, both
 * of which are already on the application.
 *
 * `unknown` is counted and reported separately on purpose. A book where
 * nothing is known reads as a book with no failures if you only count
 * `fails`, and that is exactly backwards: it is a book nobody has asked the
 * question about.
 */
export function draftExposure(
  rows: ReadonlyArray<{ verdict: DraftVerdict }>,
): DraftExposure {
  const out: DraftExposure = { ...EMPTY_DRAFT_EXPOSURE };
  for (const r of rows) {
    if (r.verdict === 'unknown') { out.unknown += 1; continue; }
    out.checked += 1;
    out[r.verdict] += 1;
  }
  return out;
}
